import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { SiAppstore, SiGoogleplay } from 'react-icons/si';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { trackProjectView, trackExternalLink } from '../utils/analytics';
import easyGiveaway from '../assets/projects/easy_giveaway.jpg';
import briefio from '../assets/projects/briedio.webp';
import artie from '../assets/projects/artie.webp';
import fitty from '../assets/projects/fitty.webp';
import skytags from '../assets/projects/skytags.webp';
import mrmoco from '../assets/projects/mrmoco.webp';
import aitopia from '../assets/projects/aitopia.webp';
import taom from '../assets/projects/taom.webp';
import zompah from '../assets/projects/zompah.png';
import wsi from '../assets/projects/wsi.webp';
import perscent from '../assets/projects/perscent.webp';
import defactofit from '../assets/projects/defactofit.webp';
import soqrateque from '../assets/projects/soqrat.png';
import vivoo from '../assets/projects/vivoo.webp';
import cleaner from '../assets/projects/cleaner.webp';

const LOCAL_IMAGES = {
  'aitopia': [aitopia],
  'defactofit': [defactofit],
  'soqrateque': [soqrateque],
  'vivoo': [vivoo],
  'cleaner': [cleaner],
  'briefio': [briefio],
  'artie': [artie],
  'easy-giveaway': [easyGiveaway],
  'fitty': [fitty],
  'mrmoco': [mrmoco],
  'taom': [taom],
  'wsi': [wsi],
  'zompah': [zompah],
  'perscent': [perscent],
  'skytags': [skytags],
};

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bg};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-x: hidden;
`;

const BackButton = styled(motion.button)`
  position: fixed;
  top: 36px;
  left: 36px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  transition: border-color 200ms ${EASE}, box-shadow 200ms ${EASE};

  svg { color: ${({ theme }) => theme.colors.text.primary}; }

  &:hover {
    border-color: rgba(255, 91, 58, 0.3);
    box-shadow: 3px 3px 0 #FF5B3A;
  }

  @media (max-width: 768px) {
    top: 20px;
    left: 20px;
    width: 44px;
    height: 44px;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 44fr 56fr;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const InfoColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  padding: 156px 72px 100px 88px;
  border-right: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.07)`};

  @media (max-width: 1024px) {
    padding: 110px 32px 48px;
    border-right: none;
  }
`;

const ImageColumn = styled(motion.div)`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 80px 72px 80px 64px;
  background: ${({ theme }) => theme.colors.surface};

  @media (max-width: 1024px) {
    position: relative;
    height: auto;
    padding: 48px 32px 60px;
  }
`;

const ImageFrame = styled.div`
  position: relative;
  width: 100%;
  max-width: ${({ $square }) => ($square ? '440px' : '960px')};
  aspect-ratio: ${({ $square }) => ($square ? '1 / 1' : '4 / 3')};
  background: ${({ theme }) => theme.colors.bg};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 0 0 1px ${({ theme }) => `rgba(${theme.colors.textRgb},0.08)`},
              0 24px 80px rgba(0, 0, 0, 0.4);
`;

const CoverImage = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: ${({ $square }) => ($square ? 'contain' : 'cover')};
  user-select: none;
  -webkit-user-drag: none;
`;

const Eyebrow = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FF5B3A;
  margin-bottom: 20px;
`;

const ProjectTitle = styled(motion.h1)`
  font-family: 'Fraunces', serif;
  font-size: clamp(3.5rem, 6vw, 6.5rem);
  font-weight: 900;
  line-height: 0.93;
  color: ${({ theme }) => theme.colors.text.primary};
  letter-spacing: -0.02em;
  margin: 0 0 32px;
`;

const ProjectTagline = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.2rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 520px;
  margin: 0 0 36px;
  padding-bottom: 36px;
  border-bottom: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.07)`};
`;

const ProjectDescription = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 520px;
  margin: 0 0 36px;
`;

const TechRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 32px;
`;

const InlineTechTag = styled.span`
  display: inline-block;
  padding: 5px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const StoreButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const StoreBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  background: ${({ $primary, theme }) => $primary ? '#FF5B3A' : theme.colors.surface};
  color: ${({ $primary, theme }) => $primary ? '#ECE8E0' : theme.colors.text.primary};
  text-decoration: none;
  border-radius: 8px;
  border: 1px solid ${({ $primary, theme }) => $primary ? 'transparent' : `rgba(${theme.colors.textRgb},0.1)`};
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 200ms ${EASE};

  &:hover {
    background: ${({ $primary, theme }) => $primary ? '#E8431F' : `rgba(${theme.colors.textRgb},0.08)`};
    border-color: ${({ $primary }) => $primary ? 'transparent' : 'rgba(255, 91, 58, 0.25)'};
  }

  &:active { transform: translateY(1px); }
`;


const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 16px;
  font-family: 'Space Grotesk', sans-serif;

  h2 {
    font-family: 'Fraunces', serif;
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
  }

  p {
    color: ${({ theme }) => theme.colors.text.muted};
    margin: 0;
  }

  button {
    margin-top: 8px;
    padding: 12px 24px;
    background: #FF5B3A;
    color: #ECE8E0;
    border: none;
    border-radius: 8px;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    cursor: pointer;
    transition: background 200ms ${EASE};

    &:hover { background: #E8431F; }
  }
`;

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentProject, setCurrentProject] = useState(null);
  const db = getFirestore();

  useEffect(() => {
    setLoading(true);
    const fetchProject = async () => {
      try {
        const snap = await getDoc(doc(db, 'projects', projectId));
        if (snap.exists()) {
          const data = snap.data();
          const iconImage = data.images?.[0] || LOCAL_IMAGES[projectId]?.[0] || null;
          const displayImage = data.coverImage || iconImage;
          const isCover = Boolean(data.coverImage);
          setCurrentProject({ id: snap.id, ...data, displayImage, isCover });
        } else {
          setCurrentProject(null);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        setCurrentProject(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId, db]);

  useEffect(() => {
    if (currentProject) trackProjectView(projectId, currentProject.title);
  }, [projectId, currentProject]);

  if (loading) {
    return (
      <PageContainer>
        <NotFoundContainer>
          <p>Loading...</p>
        </NotFoundContainer>
      </PageContainer>
    );
  }

  if (!currentProject) {
    return (
      <PageContainer>
        <NotFoundContainer>
          <h2>Project not found</h2>
          <p>This project doesn't exist or has been removed.</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </NotFoundContainer>
      </PageContainer>
    );
  }

  const isWeb = currentProject.type === 'web';

  return (
    <PageContainer>
      <BackButton
        onClick={() => navigate(-1)}
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <ArrowLeft size={20} />
      </BackButton>

      <HeroSection>
        <InfoColumn
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Eyebrow>Case Study / {isWeb ? 'Web' : 'Mobile'}</Eyebrow>
          <ProjectTitle
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          >
            {currentProject.title}
          </ProjectTitle>
          {currentProject.tagline && <ProjectTagline>{currentProject.tagline}</ProjectTagline>}
          <ProjectDescription>{currentProject.description}</ProjectDescription>

          {currentProject.technologies?.length > 0 && (
            <TechRow>
              {currentProject.technologies.map(tech => (
                <InlineTechTag key={tech}>{tech}</InlineTechTag>
              ))}
            </TechRow>
          )}

          <StoreButtons>
            {currentProject.iosLink && (
              <StoreBtn
                $primary
                href={currentProject.iosLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLink(`${currentProject.title} - App Store`, currentProject.iosLink)}
              >
                <SiAppstore size={15} /> App Store
              </StoreBtn>
            )}
            {currentProject.androidLink && (
              <StoreBtn
                href={currentProject.androidLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLink(`${currentProject.title} - Google Play`, currentProject.androidLink)}
              >
                <SiGoogleplay size={14} /> Google Play
              </StoreBtn>
            )}
            {currentProject.webLink && (
              <StoreBtn
                $primary
                href={currentProject.webLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLink(`${currentProject.title} - Website`, currentProject.webLink)}
              >
                <ExternalLink size={14} /> Visit Website
              </StoreBtn>
            )}
          </StoreButtons>
        </InfoColumn>

        <ImageColumn>
          <ImageFrame $square={!currentProject.isCover}>
            {currentProject.displayImage && (
              <CoverImage
                $square={!currentProject.isCover}
                src={currentProject.displayImage}
                alt={`${currentProject.title} ${currentProject.isCover ? 'cover' : 'icon'}`}
                loading="eager"
                decoding="async"
                fetchpriority="high"
                onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            )}
          </ImageFrame>
        </ImageColumn>
      </HeroSection>
    </PageContainer>
  );
};

export default ProjectDetail;
